import { Link } from "react-router-dom";
import { HeartHandshake, ShieldCheck, Eye, Target, Award, PenLine, Palette, BarChart3, Megaphone, Users } from "lucide-react";
import { PageHero, SectionHead, Reveal } from "@/components/site/Section";
import { ClientsMarquee } from "@/components/site/HomeExtras";

const IMGS = {
  hero: "https://images.unsplash.com/photo-1681949103006-70066fb25dfe?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400",
  creative: "https://images.unsplash.com/photo-1623652554515-91c833e3080e?crop=entropy&cs=srgb&fm=jpg&q=80&w=1000",
  copy: "https://images.unsplash.com/photo-1620325867502-221cfb5faa5f?crop=entropy&cs=srgb&fm=jpg&q=80&w=1000",
  workshop: "https://images.unsplash.com/photo-1586936893354-362ad6ae47ba?crop=entropy&cs=srgb&fm=jpg&q=80&w=1000",
  stage: "https://images.unsplash.com/photo-1587825140708-dfaf72ae4b04?crop=entropy&cs=srgb&fm=jpg&q=80&w=1000",
  event: "https://images.unsplash.com/photo-1540575467063-178a50c2df87?crop=entropy&cs=srgb&fm=jpg&q=80&w=1000",
};
const TALENT = [
  [Megaphone, "Marketing strategists", "Campaign planning, positioning and go-to-market for SMBs across industries.", "#4f46e5"],
  [PenLine, "Copywriters", "Brand storytellers who shape the voice, prompts and templates behind every AI draft.", "#db2777"],
  [Palette, "Creative designers", "Visual direction, creative briefs and ad concepts that stop the scroll.", "#7c3aed"],
  [BarChart3, "Performance & analytics", "Measurement frameworks, tracking and plain-English reporting.", "#10b981"],
  [Users, "Events & community", "Trade-show, webinar and community marketing that fills rooms.", "#f59e0b"],
  [ShieldCheck, "Data & compliance", "GDPR-aligned data handling and responsible AI review.", "#06b6d4"],
];
const VALUES = [
  [Target, "SMB-first", "Built for founders and lean teams, not enterprise marketing departments.", "#4f46e5"],
  [Eye, "Honest by default", "Clear labels on every feature, and no promises of guaranteed results.", "#db2777"],
  [HeartHandshake, "Humans approve", "AI drafts, you decide. Nothing goes live without your approval.", "#10b981"],
];

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHero eyebrow="About Sliance" title={<>Marketing expertise, <span className="text-gradient">amplified by AI</span></>}
        copy="Sliance is built by Cognitive Market Intelligence Pvt. Ltd. — a team of marketers, creatives and copywriters who turned decades of hands-on marketing into an AI platform for growing businesses." img={IMGS.hero}>
        <Link to="/contact" className="btn-brand" data-testid="about-contact-btn">Talk to our team</Link>
      </PageHero>
      <section className="py-20">
        <div className="container-x grid lg:grid-cols-12 gap-6">
          <Reveal className="lg:col-span-5">
            <div className="relative h-full overflow-hidden rounded-[1.75rem] bg-brand p-8 md:p-10 text-white grain" data-testid="founder-card">
              <Award className="h-9 w-9" />
              <div className="mt-6 font-display text-6xl font-extrabold">26+</div>
              <div className="font-mono text-xs uppercase tracking-[0.2em] text-white/80">years in marketing</div>
              <p className="mt-6 text-white/90 leading-relaxed">Our founder has led marketing for over 26 years across brand building, demand generation, digital, events and channel marketing — for start-ups, SMBs and large enterprises. Sliance packages that experience into practical AI workflows so every business can market like it has a seasoned team behind it.</p>
            </div>
          </Reveal>
          <div className="lg:col-span-7 grid grid-cols-2 gap-4">
            <img src={IMGS.creative} alt="Creative planning session" className="rounded-2xl aspect-[4/3] object-cover border" />
            <img src={IMGS.stage} alt="Corporate marketing event" className="rounded-2xl aspect-[4/3] object-cover border" />
            <img src={IMGS.copy} alt="Copywriting and planning" className="rounded-2xl aspect-[4/3] object-cover border" />
            <img src={IMGS.event} alt="Conference audience" className="rounded-2xl aspect-[4/3] object-cover border" />
          </div>
        </div>
      </section>
      <section className="py-20 border-t bg-card/40">
        <div className="container-x">
          <SectionHead eyebrow="Our people" title="Marketing talent behind every agent" copy="Our AI is trained on playbooks written by real marketers. Our in-house team brings creative, copywriting and strategic skills to templates, reviews and client success." />
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {TALENT.map(([I, t, d, c], i) => (
              <Reveal key={t} delay={i * 0.05}>
                <div className="card-box p-6 h-full lift" data-testid={`talent-card-${i}`}><div className="h-11 w-11 rounded-xl flex items-center justify-center" style={{ background: `${c}1f`, color: c }}><I className="h-5 w-5" /></div><h3 className="mt-4 font-semibold">{t}</h3><p className="mt-2 text-sm text-muted-foreground">{d}</p></div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      <section className="py-20">
        <div className="container-x grid lg:grid-cols-12 gap-8 items-center">
          <img src={IMGS.workshop} alt="Marketing workshop" className="lg:col-span-5 rounded-[1.75rem] aspect-[4/3] object-cover border" />
          <div className="lg:col-span-7">
            <SectionHead eyebrow="What we believe" title="Our principles" />
            <div className="mt-8 grid sm:grid-cols-3 gap-4">
              {VALUES.map(([I, t, d, c]) => <div key={t} className="card-box p-5"><I className="h-6 w-6" style={{ color: c }} /><h3 className="mt-3 font-semibold">{t}</h3><p className="mt-1 text-sm text-muted-foreground">{d}</p></div>)}
            </div>
          </div>
        </div>
      </section>
      <ClientsMarquee />
    </div>
  );
}
